import React, { useEffect, useState } from "react";
import { Camera, Signal, Waves } from "lucide-react";

/**
 * JerichoSurveillanceStack — right-side surveillance intel ecosystem.
 * Tighter, denser, more asymmetric than the previous version: 4 cams in
 * a varied grid, vertical intercepts column, signal bars.
 */
export const JerichoSurveillanceStack = () => {
  return (
    <section
      data-testid="jericho-surveillance-stack"
      className="relative"
    >
      {/* Camera grid — asymmetric: big tile + 3 small */}
      <div className="grid grid-cols-3 gap-1 mb-3">
        <FeedTile id="CAM-04" label="VAULT · Ω" alert big />
        <FeedTile id="CAM-01" label="INTAKE" />
        <FeedTile id="CAM-07" label="LAB" />
        <FeedTile id="CAM-09" label="PERIM-W" />
        <FeedTile id="CAM-11" label="GATE-S" />
      </div>

      {/* Intercepts */}
      <div
        className="border border-[#2A2722] bg-black/55 backdrop-blur-[2px] mb-3"
        style={{ boxShadow: "inset 0 0 24px rgba(0,0,0,0.6)" }}
      >
        <div className="px-3 py-1.5 border-b border-[#2A2722] flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Signal size={10} className="text-[#5DD4B3]" />
            <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#5DD4B3]">
              INTERCEPTS
            </span>
          </div>
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">
            ◉ LIVE
          </span>
        </div>
        <ul className="px-3 py-2 space-y-1 font-mono text-[10px] text-[#A39E93]">
          <Intercept time="03:14:08" src="STN-7" txt="packet … 1f4 / accept" />
          <Intercept time="03:14:21" src="STN-4" txt="cross-ref · op pending" tone="warn" />
          <Intercept time="03:14:33" src="STN-2" txt="vault-Ω handshake OK" tone="ok" />
          <Intercept time="03:14:47" src="STN-9" txt="dead drop confirmed" />
          <Intercept time="03:15:02" src="STN-1" txt="trace masked · routed" tone="warn" />
          <Intercept time="03:15:14" src="STN-5" txt="redaction queue +3" />
        </ul>
      </div>

      {/* Signal bands */}
      <div
        className="border border-[#2A2722] bg-black/55 backdrop-blur-[2px]"
        style={{ boxShadow: "inset 0 0 24px rgba(0,0,0,0.6)" }}
      >
        <div className="px-3 py-1.5 border-b border-[#2A2722] flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Waves size={10} className="text-[#E0A526]" />
            <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#E0A526]">
              SIGNAL · BANDS
            </span>
          </div>
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
            ◎ SCAN
          </span>
        </div>
        <div className="px-3 py-2.5">
          <SignalBar label="VHF" pct={72} tone="ok" />
          <SignalBar label="UHF" pct={48} tone="warn" />
          <SignalBar label="SAT" pct={89} tone="ok" />
          <SignalBar label="HF " pct={22} tone="alert" />
        </div>
      </div>
    </section>
  );
};

const FeedTile = ({ id, label, alert = false, big = false }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => (x + 1) % 1000), 90);
    return () => clearInterval(t);
  }, []);
  const offset = (tick % 6) - 3;
  return (
    <div
      className={
        "relative border overflow-hidden " +
        (big ? "col-span-2 row-span-2 aspect-[16/9] " : "aspect-[16/10] ") +
        (alert ? "border-[#D92D20]/70" : "border-[#2A2722]")
      }
      style={{
        boxShadow: alert
          ? "0 0 0 1px rgba(217,45,32,0.18), inset 0 0 30px rgba(0,0,0,0.85)"
          : "inset 0 0 30px rgba(0,0,0,0.85)",
      }}
    >
      {/* Noise scanlines */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, rgba(93,212,179,0.10) 0px, rgba(93,212,179,0.10) 1px, transparent 1px, transparent 2px)",
          transform: `translateY(${offset}px)`,
        }}
      />
      {/* Background tint */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#08120d] via-[#040706] to-[#020403]" />

      {/* Procedural subject blob */}
      <div
        className="absolute w-1.5 h-1.5 rounded-full bg-[#5DD4B3]/80"
        style={{
          left: `${20 + ((tick * 1.5) % 60)}%`,
          top: `${30 + Math.abs(((tick * 2) % 40) - 20)}%`,
          boxShadow: "0 0 8px rgba(93,212,179,0.6)",
        }}
      />
      {big && (
        <>
          {/* Door / corridor outline on big cam */}
          <div className="absolute inset-x-[30%] bottom-[15%] top-[35%] border border-[#5DD4B3]/30" />
          {/* Targeting crosshair */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <g stroke="#D92D20" strokeWidth="0.3" strokeOpacity="0.6">
              <line x1="50" y1="20" x2="50" y2="35" />
              <line x1="50" y1="65" x2="50" y2="80" />
              <line x1="20" y1="50" x2="35" y2="50" />
              <line x1="65" y1="50" x2="80" y2="50" />
              <circle cx="50" cy="50" r="8" fill="none" />
            </g>
          </svg>
        </>
      )}

      {/* Labels */}
      <div className="absolute top-1 left-1.5 font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5DD4B3]/90">
        {id}
      </div>
      <div className="absolute top-1 right-1.5 font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#A39E93]">
        ● REC
      </div>
      <div className="absolute bottom-1 left-1.5 font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#7a7062]">
        {label}
      </div>
      {alert && (
        <div className="absolute bottom-1 right-1.5 font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#D92D20] animate-pulse">
          ALERT
        </div>
      )}
    </div>
  );
};

const Intercept = ({ time, src, txt, tone = "default" }) => {
  const c = {
    default: "text-[#A39E93]",
    ok: "text-[#5DD4B3]",
    warn: "text-[#E0A526]",
    alert: "text-[#D92D20]",
  }[tone];
  return (
    <li className="flex items-baseline gap-2">
      <span className="text-[#5a544a] tabular-nums">{time}</span>
      <span className="text-[#7a7062]">{src}</span>
      <span className={`truncate ${c}`}>{txt}</span>
    </li>
  );
};

const SignalBar = ({ label, pct, tone }) => {
  const c = {
    ok: "bg-[#5DD4B3]",
    warn: "bg-[#E0A526]",
    alert: "bg-[#D92D20]",
  }[tone];
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] w-7">
        {label}
      </span>
      <div className="flex-1 h-1.5 bg-[#1a1814] border border-[#2A2722] relative overflow-hidden">
        <div
          className={`${c} h-full transition-all`}
          style={{ width: `${pct}%`, boxShadow: "0 0 6px rgba(255,255,255,0.15) inset" }}
        />
      </div>
      <span className="font-mono text-[9px] tabular-nums text-[#7a7062] w-7 text-right">
        {pct}
      </span>
    </div>
  );
};

export default JerichoSurveillanceStack;
